/**
 * A APLICAÇÃO — corretivo de água e tratamento sanitário.
 *
 * «O calcário e o vermífugo diferem no que se aplica, não em como se
 * registra.» Produto, dose, data, represa. Uma ficha só, com um botão em cima
 * dizendo qual dos dois.
 *
 * ── A REPRESA PODE ESTAR VAZIA ────────────────────────────────────────────
 *
 * E é quando boa parte do calcário entra: o preparo acontece entre ciclos, e o
 * vazio sanitário é justamente a represa sem peixe. Por isso esta é a única
 * tela de manejo que oferece TODAS as represas ativas, e não só as ocupadas.
 *
 * Quem amarra o ciclo é o servidor, se houver um aberto. Quem está na beira do
 * tanque escolhe a REPRESA — que é o que ele enxerga —, não o código de um
 * ciclo que ele não precisa saber de cor.
 *
 * ── A UNIDADE É ESCOLHIDA, NÃO SUPOSTA ────────────────────────────────────
 *
 * Corretivo vai em quilo; sanitário líquido vai em litro ou mililitro.
 * Converter exigiria a densidade do produto, que a fazenda não tem e que muda
 * de lote para lote. Gravar «2 litros» como «2 kg» seria o erro silencioso de
 * unidade que este projeto inteiro existe para evitar.
 *
 * ── A LISTA DE SANITÁRIOS SE ESCREVE SOZINHA ──────────────────────────────
 *
 * O gestor preferiu não ditá-la, e tem razão: eu inventaria uma farmacopeia de
 * piscicultura que não conheço. Então o campo aceita digitar, e o que já foi
 * aplicado antes aparece como sugestão. A lista da fazenda nasce do uso dela.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  listarAplicacoes, listarCiclos, listarTanques, parametrosDoCalculo, registrarAplicacao,
  type Aplicacao as Lancamento, type Ciclo, type TipoDeAplicacao, type Tanque,
  type UnidadeDeDose,
} from '../acesso/api'
import { inteiro, numero, numeroNaFaixa, soNumero } from '../dominio/unidades'
import { hojeISO, SeletorDeDia } from './SeletorDeDia'

const RASCUNHO = 'cunhatai.aplicacao'

const CORRETIVOS_PADRAO = [
  'calcário calcítico', 'calcário dolomítico', 'ureia', 'MAP', 'DAP',
  'super triplo', 'cal virgem', 'cal hidratada', 'adubo orgânico',
]

const UNIDADES: readonly UnidadeDeDose[] = ['kg', 'g', 'L', 'mL']

interface Rascunho {
  tanqueId: string
  dia: string
  tipo: TipoDeAplicacao
  produto: string
  dose: string
  unidade: UnidadeDeDose
  observacao: string
}

function lerRascunho(): Rascunho | null {
  try {
    const bruto = localStorage.getItem(RASCUNHO)
    return bruto ? (JSON.parse(bruto) as Rascunho) : null
  } catch {
    return null
  }
}

function guardarRascunho(r: Rascunho | null): void {
  try {
    if (r) localStorage.setItem(RASCUNHO, JSON.stringify(r))
    else localStorage.removeItem(RASCUNHO)
  } catch {
    /* armazenamento bloqueado: a ficha vale só enquanto a tela estiver aberta */
  }
}

export function Aplicacao({ aoGravar }: { aoGravar: () => void }) {
  const salvo = useMemo(lerRascunho, [])

  const [tanques, setTanques] = useState<Tanque[]>([])
  const [ciclos, setCiclos] = useState<Ciclo[]>([])
  const [corretivos, setCorretivos] = useState<string[]>(CORRETIVOS_PADRAO)
  const [historico, setHistorico] = useState<Lancamento[]>([])
  const [tanqueId, setTanqueId] = useState(salvo?.tanqueId ?? '')
  const [dia, setDia] = useState(salvo?.dia ?? hojeISO())
  const [tipo, setTipo] = useState<TipoDeAplicacao>(salvo?.tipo ?? 'corretivo')
  const [produto, setProduto] = useState(salvo?.produto ?? '')
  const [dose, setDose] = useState(salvo?.dose ?? '')
  const [unidade, setUnidade] = useState<UnidadeDeDose>(salvo?.unidade ?? 'kg')
  const [observacao, setObservacao] = useState(salvo?.observacao ?? '')
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    Promise.all([listarTanques(), listarCiclos(), parametrosDoCalculo(), listarAplicacoes()])
      .then(([t, c, p, a]) => {
        setTanques(t.filter((x) => x.ativo))
        // TODOS os ciclos, encerrados inclusive: quem ocupava o tanque na data
        // da aplicação pode ser um ciclo que já fechou.
        setCiclos(c)
        setHistorico(a)
        const lista = (p.parametros.find((x) => x.chave === 'aplicacao.corretivos')?.valor ?? '')
          .split(';')
          .map((x) => x.trim())
          .filter((x) => x !== '')
        if (lista.length > 0) setCorretivos(lista)
        setErro(null)
      })
      .catch((falha: unknown) => {
        setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
      })
      .finally(() => setCarregando(false))
  }, [dia])

  useEffect(() => {
    const temAlgo = produto !== '' || dose !== ''
    guardarRascunho(temAlgo ? { tanqueId, dia, tipo, produto, dose, unidade, observacao } : null)
  }, [tanqueId, dia, tipo, produto, dose, unidade, observacao])

  /**
   * Quem ocupava cada tanque NO DIA ESCOLHIDO — o mesmo critério do servidor.
   *
   * A tela dizia «vazia» por ciclo aberto HOJE, e o servidor amarra o ciclo que
   * ocupava o tanque NA DATA. Num lançamento atrasado os dois discordavam: a
   * dica prometia «fica no tanque, sem ciclo» e a aplicação ia parar no ciclo
   * que já tinha encerrado. A tela precisa prometer o que será gravado.
   *
   * Encerramento no mesmo dia não exclui, e entre dois candidatos vale o de
   * início mais recente — as duas regras são as da consulta do servidor.
   */
  const ocupados = useMemo(() => {
    const dono = new Map<string, Ciclo>()
    for (const c of ciclos) {
      if (c.data_inicio > dia) continue
      if (c.data_encerramento !== null && c.data_encerramento < dia) continue
      const atual = dono.get(c.tanque_id)
      if (!atual || c.data_inicio > atual.data_inicio) dono.set(c.tanque_id, c)
    }
    return new Map([...dono].map(([tanqueId, c]) => [tanqueId, c.codigo]))
  }, [ciclos, dia])

  /** Os sanitários que a fazenda já usou — a lista que se escreve sozinha. */
  const sanitariosUsados = useMemo(() => {
    const vistos: string[] = []
    for (const a of historico) {
      if (a.tipo === 'sanitario' && !vistos.includes(a.produto)) vistos.push(a.produto)
    }
    return vistos
  }, [historico])

  const nDose = numeroNaFaixa(dose, 0.001, 100_000)
  const tanque = tanques.find((t) => t.id === tanqueId) ?? null

  if (carregando) return <p className="aviso">Carregando…</p>
  // Sem resposta do servidor o app não sabe quantas represas existem, e dizer
  // «nenhuma» seria afirmar o que ele não sabe. Dentro do ramo porque `erro`
  // também guarda falha de gravação.
  if (tanques.length === 0) {
    return erro !== null ? (
      <p className="aviso-erro">Não deu para carregar as represas: {erro}</p>
    ) : (
      <p className="aviso">Nenhuma represa cadastrada.</p>
    )
  }

  const pronto = tanque !== null && produto.trim() !== '' && nDose !== null

  function trocarTipo(novo: TipoDeAplicacao) {
    if (novo === tipo) return
    setTipo(novo)
    // O nome do produto não sobrevive à troca: «calcário» num sanitário seria
    // um registro errado com cara de certo.
    setProduto('')
    setUnidade(novo === 'corretivo' ? 'kg' : 'L')
  }

  function gravar() {
    if (!tanque || nDose === null) return
    setErro(null)
    try {
      registrarAplicacao(
        {
          tanque_id: tanque.id,
          data: dia,
          tipo,
          produto: produto.trim(),
          dose: nDose,
          unidade,
          observacao: observacao.trim() === '' ? null : observacao.trim(),
        },
        { rotulo: tanque.codigo },
      )
      setDose('')
      setObservacao('')
      guardarRascunho(null)
      aoGravar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
    }
  }

  return (
    <section className="bloco">
      <h2>Aplicação</h2>

      <div className="apuracao">
        <button
          type="button"
          className={tipo === 'corretivo' ? 'unidade-botao ativo' : 'unidade-botao'}
          onClick={() => trocarTipo('corretivo')}
        >
          corretivo de água
        </button>
        <button
          type="button"
          className={tipo === 'sanitario' ? 'unidade-botao ativo' : 'unidade-botao'}
          onClick={() => trocarTipo('sanitario')}
        >
          sanitário
        </button>
      </div>

      <label className="campo">
        <span className="campo-rotulo">Em qual represa?</span>
        <select value={tanqueId} onChange={(e) => setTanqueId(e.target.value)}>
          <option value="">escolha…</option>
          {tanques.map((t) => (
            <option key={t.id} value={t.id}>
              {t.codigo}
              {ocupados.has(t.id) ? ` · ${ocupados.get(t.id)}` : ' · vazia'}
            </option>
          ))}
        </select>
        {tanque && !ocupados.has(tanque.id) && (
          <span className="campo-dica">
            Represa vazia nesta data. O registro fica no tanque, sem ciclo — é assim que o
            preparo entre lotes aparece no histórico.
          </span>
        )}
      </label>

      <SeletorDeDia dia={dia} aoMudar={setDia} />

      {/* ---- O PRODUTO -------------------------------------------------- */}
      {tipo === 'corretivo' ? (
        <label className="campo">
          <span className="campo-rotulo">Produto</span>
          <select value={produto} onChange={(e) => setProduto(e.target.value)}>
            <option value="">escolha…</option>
            {corretivos.map((x) => (
              <option key={x} value={x}>{x}</option>
            ))}
          </select>
        </label>
      ) : (
        <>
          <label className="campo">
            <span className="campo-rotulo">Produto</span>
            <input
              value={produto}
              onChange={(e) => setProduto(e.target.value)}
              placeholder="vermífugo, sal, formol…"
              list="sanitarios-usados"
            />
            <span className="campo-dica">
              Digite o nome. O que já foi usado antes aparece como sugestão.
            </span>
          </label>
          <datalist id="sanitarios-usados">
            {sanitariosUsados.map((x) => (
              <option key={x} value={x} />
            ))}
          </datalist>
        </>
      )}

      {/* ---- A DOSE ----------------------------------------------------- */}
      <label className="campo">
        <span className="campo-rotulo">Dose aplicada</span>
        <input
          value={dose}
          onChange={(e) => setDose(soNumero(e.target.value))}
          inputMode="decimal"
          placeholder="1.500"
        />
      </label>

      <div className="apuracao compacta">
        {UNIDADES.map((u) => (
          <button
            key={u}
            type="button"
            className={unidade === u ? 'unidade-botao ativo' : 'unidade-botao'}
            onClick={() => setUnidade(u)}
          >
            {u}
          </button>
        ))}
      </div>

      {nDose !== null && tanque && tanque.area_m2 > 0 && unidade === 'kg' && (
        <p className="secundario">
          {numero((nDose / tanque.area_m2) * 10_000, 0)} kg por hectare, nos{' '}
          {inteiro(tanque.area_m2)} m² desta represa.
        </p>
      )}

      <label className="campo">
        <span className="campo-rotulo">Observação (opcional)</span>
        <input
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="água turva depois da chuva"
        />
      </label>

      {erro && <p className="aviso-erro">{erro}</p>}

      <button type="button" className="confirma" disabled={!pronto} onClick={gravar}>
        ✓ registrar aplicação
      </button>

      {historico.length > 0 && (
        <>
          <h3>Últimas aplicações</h3>
          <ul className="fila-lista">
            {historico.slice(0, 12).map((a) => (
              <li key={a.id} className="fila-item">
                <div className="fila-texto">
                  {/* Casa decimal só quando existe: «1.500,00 kg» de calcário
                      é ruído, e «0,25 L» de sanitário precisa das duas. */}
                  <strong>{a.produto}</strong> · {numero(a.dose, a.dose % 1 === 0 ? 0 : 2)}{' '}
                  {a.unidade}
                  <br />
                  <span className="secundario">
                    {diaCurto(a.data)} · {a.tanque}
                    {a.ciclo ? ` · ${a.ciclo}` : ' · represa vazia'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

/** «18/09» — o suficiente para saber de quando é. */
function diaCurto(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}`
}
